import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { markedSmartypants } from 'marked-smartypants';
import { createEffect } from 'solid-js';
import EmbedComponent from './embeds';
import { ZoomableImage } from './ZoomableImage';
import { WIDGET_TYPE } from '@/types/constants';
import { getUtmParameters } from '@/utils/utm';
import { getContentType } from '@/utils/getContentType';

export interface Props {
  message: string;
  isStreaming: boolean;
  widgetType?: WIDGET_TYPE;
}

const videoContentTypePrefix = '[Content: Video]';
const videoContentTypePrefixRegexp = new RegExp('^\\' + videoContentTypePrefix);

export const FormattedMessage = (props: Props) => {
  let markdown!: HTMLDivElement | undefined;

  const injectMarkdown = async (content: string, isStreaming: boolean) => {
    if (!markdown) {
      return;
    }

    marked.use({
      ...markedSmartypants(),
      ...{
        async: true,
        async walkTokens(token) {
          // Marked does not support async rendering. Thus, perform content type
          // check for attachment link on tokenisation before rendering the content
          // by ways of adding prefix on the text.
          if (token.type === 'image' || token.type === 'link') {
            if (token.href) {
              const isAttachment = token.href.includes(
                '/api/attachments.public.redirect',
              );

              if (isAttachment) {
                const attachmentId = token.href.split('id=')[1];

                if (attachmentId) {
                  try {
                    const contentType = await getContentType(attachmentId);

                    if (contentType === 'video') {
                      token.text = `${videoContentTypePrefix} ${token.text}`;
                    }
                  } catch {
                    // Silently failing it and do nothing as there is no immediate
                    // way to resolve this issue except to sort out the content
                  }
                }
              }
            }
          }
        },
      },
    });

    // Custom renderer to handle image tags
    const renderer = new marked.Renderer();

    // render link tags
    renderer.link = (href, title, text) => {
      // if streaming, return a placeholder
      if (isStreaming) {
        return `<div class="link-placeholder">${text}</div>`;
      }

      const isVideoContentType = text.startsWith(videoContentTypePrefix);
      const parsedText = isVideoContentType
        ? text.replace(videoContentTypePrefixRegexp, '').trim()
        : text;

      // if the link is an embed, render the embed component
      if (href) {
        const embedComponent = EmbedComponent(href, isVideoContentType);
        if (embedComponent) {
          return embedComponent;
        }
      }

      const urlWithUtm = getUtmParameters(href, props.widgetType);

      return (
        `<a href="${urlWithUtm}"` +
        (title ? ` title="${title}"` : '') +
        ` target="_blank" rel="noopener noreferrer">${parsedText}</a>`
      );
    };

    // render image tags
    renderer.image = (href, title, text) => {
      if (isStreaming) {
        return `<div class="image-placeholder">${text}</div>`;
      }

      const isVideoContentType = text.startsWith(videoContentTypePrefix);
      const parsedText = isVideoContentType
        ? text.replace(videoContentTypePrefixRegexp, '').trim()
        : text;

      const embedComponent = EmbedComponent(href, isVideoContentType);

      if (embedComponent) {
        return embedComponent;
      }

      return ZoomableImage({
        src: href,
        alt: parsedText || title || '',
      });
    };

    const parsed = await marked.parse(content, {
      gfm: true,
      breaks: true,
      renderer,
    });

    // Configure DOMPurify to allow necessary tags and attributes
    const config = {
      ADD_TAGS: [
        'iframe',
        'img',
        'figure',
        'figcaption',
        'button',
        'svg',
        'path',
      ],
      ADD_ATTR: [
        'src',
        'width',
        'height',
        'allow',
        'allowfullscreen',
        'frameborder',
        'sandbox',
        'alt',
        'title',
        'onerror',
        'onload',
        'onclick',
        'class',
        'style',
        'aria-label',
        'viewBox',
        'fill',
        'xmlns',
        'stroke',
        'stroke-width',
        'stroke-linecap',
        'stroke-linejoin',
      ],
    };

    const sanitized = DOMPurify.sanitize(parsed as string, config);
    if (markdown) {
      markdown.innerHTML = sanitized;

      // Add click handlers for zoom functionality
      const images = markdown.querySelectorAll('.zoomable-image-container img');
      images.forEach((img) => {
        img.addEventListener('click', (e) => {
          e.preventDefault();
          const button = (e.target as HTMLElement).closest('button');
          if (button) {
            button.click();
          }
        });
      });
    }
  };

  createEffect(() => {
    const initMarkdownParser = async () => {
      await injectMarkdown(props.message, props.isStreaming);
    };

    initMarkdownParser();
  });

  return <div class="flex-1 bf-markdown markdown-body" ref={markdown} />;
};
